import axios from "axios";
import api from "../utils/api.js";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { refreshAccessToken } from "../features/auth/services/authApi.js";

const AuthContext = createContext();
const apiUrl = process.env.REACT_APP_API_URL?.trim();

// Configure axios to send cookies with requests (needed for HTTP-only refresh token cookie)
axios.defaults.withCredentials = true;

const AuthProvider = ({ children }) => {
  // Only access token stored in localStorage; refresh token is in HTTP-only cookie (managed by backend)
  const initialAccessToken = localStorage.getItem("accessToken");
  if (initialAccessToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${initialAccessToken}`;
  }

  const [accessToken, setAccessToken_] = useState(initialAccessToken);
  const [user, setUser] = useState(null);
  const refreshTimerRef = useRef(null);

  // Decode JWT to extract expiry time
  const decodeTokenExpiry = (token) => {
    if (!token) return null;
    try {
      const parts = token.split(".");
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
      return payload?.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  // Refresh access token when expired (called by interceptor or manual trigger)
  // Refresh token is automatically sent via HTTP-only cookie
  const refreshTokens = async () => {
    if (!apiUrl) return null;
    try {
      const res = await refreshAccessToken();
      setAccessToken_(res.accessToken);
      return res.accessToken;
    } catch (err) {
      // Refresh failed, clear auth state
      setAccessToken_(null);
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      localStorage.setItem("accessToken", accessToken);

      // Fetch current user using access token
      if (apiUrl) {
        axios
          .get(`${apiUrl}/users/me`)
          .then((res) => setUser(res.data))
          .catch(() => {
            // If token invalid, clear auth state
            setUser(null);
            setAccessToken_(null);
          });
      }

      // Schedule proactive refresh shortly before token expires
      const expiryMs = decodeTokenExpiry(accessToken);
      if (expiryMs) {
        const now = Date.now();
        const bufferMs = 60_000; // Refresh 1 minute before expiry
        const delay = Math.max(expiryMs - now - bufferMs, 5_000); // Minimum 5 seconds

        // Clear any existing timer
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }

        refreshTimerRef.current = setTimeout(() => {
          console.log("[authProvider] Auto-refreshing access token before expiry");
          refreshTokens();
        }, delay);
      }
    } else {
      delete axios.defaults.headers.common.Authorization;
      delete api.defaults.headers.common.Authorization;
      localStorage.removeItem("accessToken");
      setUser(null);

      // Clear refresh timer when logged out
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    // Cleanup timer on unmount or token change
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [accessToken]);

  const value = useMemo(
    () => ({
      accessToken,
      setAccessToken: setAccessToken_,
      user,
      setUser,
      refreshTokens,
      // For backwards compatibility
      token: accessToken,
      setToken: setAccessToken_,
    }),
    [accessToken, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
export default AuthProvider;
